import PropTypes from "prop-types";
import React, { useMemo } from "react";
import { AllowedAccess as LibraryAllowedAccess } from "react-permission-role";

/**
 * `AllowedAccess` with a dependency the library can compare.
 *
 * react-permission-role@2 runs its check inside `useEffect(..., [roles,
 * permissions])` and sets two pieces of state there. Those props are arrays,
 * and every call site writes one as a JSX literal — a new array on each render.
 * So the effect saw a changed dependency every time, set state, caused the next
 * render, and React stopped it at fifty passes with "Maximum update depth
 * exceeded". Every guarded block on the screen did this, on every render.
 *
 * The lists themselves never change: they are fixed sets of permission names.
 * Memoising them by content — not by identity — gives the effect something
 * stable to compare, and the check runs once. Call sites keep writing the
 * literal they always wrote; the fix lives here rather than in each of them.
 */
export const AllowedAccess = ({ roles, permissions, ...rest }) => {
  const rolesKey = Array.isArray(roles) ? roles.join("|") : "";
  const permissionsKey = Array.isArray(permissions)
    ? permissions.join("|")
    : "";

  const stableRoles = useMemo(() => roles, [rolesKey]);
  const stablePermissions = useMemo(() => permissions, [permissionsKey]);

  return (
    <LibraryAllowedAccess
      roles={stableRoles}
      permissions={stablePermissions}
      {...rest}
    />
  );
};

AllowedAccess.propTypes = {
  roles: PropTypes.arrayOf(PropTypes.string),
  permissions: PropTypes.arrayOf(PropTypes.string),
};

export default AllowedAccess;
